import { Body, Controller, Delete, Get, Param, Patch, Post, Put, UseGuards, ValidationPipe } from '@nestjs/common';
import { AuthGuard } from '@nestjs/passport';
import { ApiBearerAuth, ApiBody, ApiParam, ApiTags } from '@nestjs/swagger';
import { RequireModule } from 'src/auth/decorators/require-module.decorator';
import { RequirePermission } from 'src/auth/decorators/require-permission.decorator';
import { ModuleGuard } from 'src/auth/guards/module.guard';
import { PermissionGuard } from 'src/auth/guards/permission.guard';
import type { LoginUserInfo } from '../auth/dto/login.dto';
import { GetUser } from '../auth/get-user.decorator';
import { CreatePresentacionDto } from './dto/create-presentacion.dto';
import { UpdatePresentacionDto } from './dto/update-presentacion.dto';
import { ProductoPresentacionesService } from './producto-presentaciones.service';

@ApiTags('Producto Presentaciones')
@ApiBearerAuth()
@UseGuards(AuthGuard('jwt'), ModuleGuard)
@RequireModule('INVENTARIO')
@Controller({ version: '1', path: 'producto-presentaciones' })
export class ProductoPresentacionesController {
  constructor(private readonly service: ProductoPresentacionesService) {}

  @Post()
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_ATR_PRESENTACION_CREAR')
  @ApiBody({ type: CreatePresentacionDto })
  async create(@Body(ValidationPipe) dto: CreatePresentacionDto, @GetUser() user: LoginUserInfo) {
    return this.service.create(dto, user.empresa_id, user.id);
  }

  @Get('producto/:productoId')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_ATR_PRESENTACION_VER')
  @ApiParam({ name: 'productoId', description: 'ID del producto' })
  async findByProducto(@Param('productoId') productoId: string) {
    return this.service.findByProducto(productoId);
  }

  @Get(':id')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_ATR_PRESENTACION_VER')
  @ApiParam({ name: 'id', description: 'ID de la presentación' })
  async findOne(@Param('id') id: string) {
    return this.service.findOne(id);
  }

  @Put(':id')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_ATR_PRESENTACION_EDITAR')
  @ApiParam({ name: 'id', description: 'ID de la presentación' })
  @ApiBody({ type: UpdatePresentacionDto })
  async update(@Param('id') id: string, @Body(ValidationPipe) dto: UpdatePresentacionDto, @GetUser() user: LoginUserInfo) {
    return this.service.update(id, dto, user.empresa_id, user.id);
  }

  @Patch(':id/toggle')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_ATR_PRESENTACION_EDITAR')
  @ApiParam({ name: 'id', description: 'ID de la presentación' })
  async toggleActive(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.toggleActive(id, user.empresa_id, user.id);
  }

  @Delete(':id')
  @UseGuards(PermissionGuard)
  @RequirePermission('INVENTARIO', 'INV_ATR_PRESENTACION_ELIMINAR')
  @ApiParam({ name: 'id', description: 'ID de la presentación' })
  async remove(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.remove(id, user.empresa_id, user.id);
  }
}
